import { useState, useCallback } from 'react';
import { isEmpty } from 'lodash';
import {
    createOrganization as createOrganizationService,
    acceptInvitation as businessAcceptInvitation,
} from '@fiverr-private/pro_migration';
import { BUSINESS_TRIGGER_CTA } from '@fiverr-private/business_success_sign_modal';
import { useIdentificationContext } from '@fiverr-private/identification_core';
import { logError } from '../../utils/logError';
import { globalBigQueryReporter, globalGrafanaReporter } from '../../events';
import { checkEmailInvitation, type InvitationData } from '../../services/checkEmailInvitation';
import { useStepsContext } from '../StepsContext';
import { createUser, buildRedirectUrl, type SignUpProps } from './useSignUpActions.utils';

type SignUpParams = Omit<SignUpProps, 'inBeetlesMigrateSignupWebRollout' | 'inBeetlesMigrateSocialSignUpRollout'>;

export const useSignUpActions = () => {
    const {
        source,
        setRedirectUrl: setMainRedirectUrl,
        navigateToRedirectUrl,
        inBeetlesMigrateSignupWebRollout,
        inBeetlesMigrateSocialSignUpRollout,
    } = useIdentificationContext();
    const { goToStep } = useStepsContext();
    const [organizationId, setOrganizationId] = useState<string | undefined>();
    const [redirectUrl, setLocalRedirectUrl] = useState<string | undefined>();
    const [invitationData, setInvitationData] = useState<InvitationData | null>(null);

    const updateRedirectUrl = useCallback(
        (updatedRedirectUrl: string) => {
            setLocalRedirectUrl(updatedRedirectUrl);
            setMainRedirectUrl(updatedRedirectUrl);
            return Promise.resolve();
        },
        [setLocalRedirectUrl, setMainRedirectUrl]
    );

    const signUp = useCallback(
        async ({ isSocialFlow, email, password, username, firstName, lastName }: SignUpParams) => {
            try {
                if (isSocialFlow && !email) {
                    logError({
                        message: 'Email is missing on social submit',
                    });
                    return;
                }

                const user = await createUser({
                    email,
                    password,
                    username,
                    firstName,
                    lastName,
                    isSocialFlow,
                    inBeetlesMigrateSignupWebRollout,
                    inBeetlesMigrateSocialSignUpRollout,
                });

                if (user?.redirectUrl) {
                    await updateRedirectUrl(user?.redirectUrl);
                }

                if (isEmpty(user?.errorKeys)) {
                    globalGrafanaReporter.count('identification.%{platform}.signup.completed');
                }

                return user;
            } catch (err) {
                globalBigQueryReporter.send('identification.failed');
                globalGrafanaReporter.count('identification.%{platform}.signup.failed');

                logError({
                    message: 'Identification Business signup failed',
                    error: err,
                });

                throw err;
            }
        },
        [updateRedirectUrl, inBeetlesMigrateSignupWebRollout, inBeetlesMigrateSocialSignUpRollout]
    );

    const createOrganization = useCallback(async () => {
        globalBigQueryReporter.send('identification.create_organization.submit');
        globalGrafanaReporter.count('identification.%{platform}.create_organization.submit');

        try {
            const { organizationId } = await createOrganizationService({
                migrateUser: false,
                source: 'useSignUpActions',
            });

            if (organizationId) {
                setOrganizationId(organizationId);

                globalBigQueryReporter.enrichContext({ organizationId });
                globalBigQueryReporter.send('identification.create_organization.completed');
                globalGrafanaReporter.count('identification.%{platform}.create_organization.completed');

                const updatedRedirectUrl = buildRedirectUrl({
                    redirectUrl,
                    source,
                });
                await updateRedirectUrl(updatedRedirectUrl);

                goToStep('business_info', { resetHistory: true });
            }
        } catch (err) {
            globalBigQueryReporter.send('identification.create_organization.failed');
            globalGrafanaReporter.count('identification.%{platform}.create_organization.failed');

            logError({
                message: 'Identification Business create_organization failed',
                error: err,
            });

            throw err;
        }
    }, [goToStep, redirectUrl, source, updateRedirectUrl]);

    const acceptInvitation = useCallback(
        async ({ withRedirect } = { withRedirect: false }) => {
            if (!invitationData) {
                return;
            }

            globalBigQueryReporter.send('identification.invitation.submit');
            globalGrafanaReporter.count('identification.%{platform}.invitation.submit');

            try {
                await businessAcceptInvitation({
                    invitationId: invitationData.invitationId,
                    organizationId: invitationData.organizationId,
                    needsMigration: true,
                    source: 'useSignUpActions',
                });

                globalBigQueryReporter.send('identification.invitation.completed');
                globalGrafanaReporter.count('identification.%{platform}.invitation.completed');

                if (withRedirect) {
                    const updatedRedirectUrl = buildRedirectUrl({
                        redirectUrl,
                        source,
                        type: BUSINESS_TRIGGER_CTA.INVITE,
                    });
                    await updateRedirectUrl(updatedRedirectUrl);
                    navigateToRedirectUrl();
                }
            } catch (err) {
                globalBigQueryReporter.send('identification.invitation.failed');
                globalGrafanaReporter.count('identification.%{platform}.invitation.failed');

                logError({
                    message: 'Identification Business invitation failed',
                    error: err,
                });

                throw err;
            }
        },
        [invitationData, redirectUrl, source, updateRedirectUrl, navigateToRedirectUrl]
    );

    const checkInvitation = useCallback(async (email: string) => {
        try {
            const data = await checkEmailInvitation(email, 'useSignUpActions');
            if (data) {
                setInvitationData(data);
                globalBigQueryReporter.send('identification.check_invitation.completed');
                globalGrafanaReporter.count('identification.%{platform}.check_invitation.completed');
                return data;
            }
        } catch (err) {
            globalBigQueryReporter.send('identification.check_invitation.failed');
            globalGrafanaReporter.count('identification.%{platform}.check_invitation.failed');

            logError({
                message: 'Identification Business check_invitation failed',
                error: err,
            });
        }
    }, []);

    return {
        organizationId,
        invitationData,
        signUp,
        createOrganization,
        checkInvitation,
        acceptInvitation,
    };
};
